/* eslint-disable react/no-children-prop */
import { Fragment, useEffect } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import AmountContainer from "app/pages/.shared/AmountContainer.jsx";
import List from "app/pages/.shared/List.jsx";
import Separator from "app/pages/.shared/Separator";
import {
	MAX_PAYMENT_INSTALMENTS_WITH_FEES_DISPLAYED,
	PAYMENT_OPTIONS,
	USABLE_CREDITS_STATUS,
} from "app/constants";
import QuotationItemsContainer from "app/pages/Booking/Quotation/PriceDetails/QuotationItems/QuotationItemsContainer";
import PayNowContainer from "app/pages/.shared/PayNow/PayNowContainer";
import Total from "app/pages/.shared/PriceSummary/Total/Total";
import TotalNote from "app/pages/.shared/PriceSummary/TotalNote/TotalNote";
import ExtraPriceDetailsContainer from "app/pages/Booking/Quotation/PriceDetails/ExtraPriceDetails/ExtraPriceDetailsContainer";
import "./PriceDetails.scss";
import { sendTagOnTotalToPayZero } from "app/utils/analytics";
import FlexAsideButtonContainer from "app/pages/Booking/Quotation/FlexRate/FlexAsideButton";
import IconArrowDown from "app/pages/.shared/static/icons/IconArrowDown";
import Collapse from "rc-collapse";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";

const PriceDetails = ({
	total,
	hasChildren,
	included,
	notIncluded,
	isPagePayment,
	mensualite,
	coutCredit,
	partialPayment,
	paymentMode,
	isBookingEligibleForDeposit,
	usableCouponsStatus,
}) => {
	const isCalculatingPrices =
		(usableCouponsStatus === USABLE_CREDITS_STATUS.LOADING_CREDIT_NOTES ||
			usableCouponsStatus === USABLE_CREDITS_STATUS.LOADING_COUPONS) &&
		isPagePayment;

	const is2xPayment = paymentMode === PAYMENT_OPTIONS["2x"];

	const shouldDisplayMultipleInstalmentsFees =
		paymentMode <= MAX_PAYMENT_INSTALMENTS_WITH_FEES_DISPLAYED &&
		paymentMode > PAYMENT_OPTIONS["2x"];

	const totalWithCreditFees =
		paymentMode >= PAYMENT_OPTIONS["4x"] && isPagePayment ? total + coutCredit : total;
	useEffect(() => {
		if (total === 0) {
			sendTagOnTotalToPayZero();
		}
	}, [total]);

	return (
		<div className="price-detail" data-testid="price-detail">
			<div className="price-detail__list">
				<div className="price-detail__list-content">
					<QuotationItemsContainer />
					<ExtraPriceDetailsContainer />
				</div>

				<Separator design="middle" />

				<div className="price-detail__item" data-testid="price-detail-total">
					<Total
						total={totalWithCreditFees}
						isBookingEligibleForDeposit={isBookingEligibleForDeposit}
						isPagePayment={isPagePayment}
						paymentMode={paymentMode}
						isLoading={isCalculatingPrices}
						isPaymentSummaryLabel={true}
					/>
				</div>

				<FlexAsideButtonContainer />

				<TotalNote showChildrenNote={hasChildren} />

				{/*	label multiple instalment payments */}
				{shouldDisplayMultipleInstalmentsFees && isPagePayment && !isCalculatingPrices && (
					<Fragment>
						<Separator design="middle" />
						<div className="price-detail__item price-detail__item--total">
							<div className="price-detail__label">
								<FormattedMessage id="my.bookings.pay.now.label" />
							</div>
							<div className="price-detail__value price-detail__value-with-deposit">
								<AmountContainer amount={mensualite} />
							</div>
						</div>
					</Fragment>
				)}

				{/*	label payment 2x sans deposit */}
				{!isBookingEligibleForDeposit &&
					is2xPayment &&
					isPagePayment &&
					!isCalculatingPrices && (
						<Fragment>
							<Separator design="middle" />
							<div className="price-detail__item price-detail__item--total">
								<div className="price-detail__label">
									<FormattedMessage id="my.bookings.pay.now.label" />
								</div>
								<div className="price-detail__value price-detail__value-with-deposit">
									<AmountContainer amount={partialPayment.payNowAmount} />
								</div>
							</div>
						</Fragment>
					)}

				{/*	label payment 2x avec deposit */}
				{isBookingEligibleForDeposit &&
					is2xPayment &&
					isPagePayment &&
					!isCalculatingPrices && (
						<Fragment>
							<Separator design="middle" />
							<div className="price-detail-with-deposit">
								<PayNowContainer
									isPaymentPage={isPagePayment}
									withDepositIcon={false}
									mensualite2Fois={partialPayment}
									isPaymentPriceDetails
								/>
							</div>
						</Fragment>
					)}

				<div className="price-detail__footer">
					<Collapse
						className="price-detail__collapse"
						expandIcon={({ isActive }) => {
							return (
								<IconArrowDown
									style={{
										transform: `rotate(${isActive ? 180 : 0}deg)`,
									}}
								/>
							);
						}}
					>
						<Collapse.Panel
							headerClass="price-detail__collapse-header"
							header={
								<div className="price-detail__collapse-title">
									<FormattedMessage id="price.detail.included.show.more.label" />
								</div>
							}
						>
							<div className="included">
								<div className="included__list">
									<Typography
										variant={TYPOGRAPHY_VARIANTS.REGULAR}
										isBold
										className="included__title"
									>
										<FormattedMessage id="price.detail.included.title" /> :
									</Typography>
									<List items={included} enableHTMLFormatting={true} />
								</div>
								<div className="included__list">
									<Typography
										variant={TYPOGRAPHY_VARIANTS.REGULAR}
										isBold
										className="included__title"
									>
										<FormattedMessage id="price.detail.not.included.title" /> :
									</Typography>
									<List items={notIncluded} enableHTMLFormatting={true} />
								</div>
							</div>
						</Collapse.Panel>
					</Collapse>
				</div>
			</div>
		</div>
	);
};

PriceDetails.propTypes = {
	total: PropTypes.number,
	isPagePayment: PropTypes.bool,
	hasChildren: PropTypes.bool,
	included: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	notIncluded: PropTypes.arrayOf(PropTypes.string),
	mensualite: PropTypes.number,
	coutCredit: PropTypes.number,
	partialPayment: PropTypes.shape({
		dueDate: PropTypes.number,
		payNowAmount: PropTypes.number,
		payLaterAmount: PropTypes.number,
	}),
	paymentMode: PropTypes.number,
	usableCouponsStatus: PropTypes.string,
	isBookingEligibleForDeposit: PropTypes.bool,
};

export default PriceDetails;
